export type TagLabelColor = 'black' | 'white';

const DARK_LABEL_Y = 0.0041737;
const LIGHT_LABEL_Y = 1.0;

const MAIN_TRC = 2.4;
const R_CO = 0.2126729;
const G_CO = 0.7151522;
const B_CO = 0.072175;

const NORM_BG = 0.56;
const NORM_TXT = 0.57;
const REV_TXT = 0.62;
const REV_BG = 0.65;

const BLK_THRS = 0.022;
const BLK_CLMP = 1.414;
const SCALE_BOW = 1.14;
const SCALE_WOB = 1.14;
const LO_BOW_OFFSET = 0.027;
const LO_WOB_OFFSET = 0.027;
const DELTA_Y_MIN = 0.0005;
const LO_CLIP = 0.1;

// Normalized 6-digit hex -> forced 'black' | 'white', consulted before APCA.
const TAG_LABEL_OVERRIDES: Record<string, TagLabelColor> = {};

// What a translucent chip is painted on, and so what its alpha reveals.
const TAG_SURFACE_HEX = 'ffffff';

// Accepts #RGB, #RGBA, #RRGGBB and #RRGGBBAA; returns 6 or 8 digits.
const normalizeHex = (color: string): string | null => {
	if (!color) {
		return null;
	}
	let hex = color.trim().replace('#', '').toLowerCase();
	if (hex.length === 3 || hex.length === 4) {
		hex = hex.replace(/./g, (digit) => digit + digit);
	}
	return /^([0-9a-f]{6}|[0-9a-f]{8})$/.test(hex) ? hex : null;
};

const hexToRgb = (hex: string): [number, number, number] => [
	parseInt(hex.substring(0, 2), 16),
	parseInt(hex.substring(2, 4), 16),
	parseInt(hex.substring(4, 6), 16),
];

// The colour an eye actually sees: an alpha channel means part of the surface
// shows through, and that mix is what the label has to contrast against.
const flattenHex = (hex: string, surfaceHex: string): string => {
	if (hex.length !== 8) {
		return hex.substring(0, 6);
	}
	const alpha = parseInt(hex.substring(6, 8), 16) / 255;
	const foreground = hexToRgb(hex);
	const surface = hexToRgb(surfaceHex);
	return foreground
		.map((channel, index) =>
			Math.round(channel * alpha + surface[index] * (1 - alpha))
				.toString(16)
				.padStart(2, '0')
		)
		.join('');
};

const srgbToY = (rgb: [number, number, number]): number => {
	const [r, g, b] = rgb.map((value) => Math.pow(value / 255, MAIN_TRC));
	return R_CO * r + G_CO * g + B_CO * b;
};

const clampBlack = (y: number): number =>
	y > BLK_THRS ? y : y + Math.pow(BLK_THRS - y, BLK_CLMP);

const apcaContrast = (textY: number, backgroundY: number): number => {
	const txt = clampBlack(textY);
	const bg = clampBlack(backgroundY);
	if (Math.abs(bg - txt) < DELTA_Y_MIN) {
		return 0;
	}
	if (bg > txt) {
		const sapc = (Math.pow(bg, NORM_BG) - Math.pow(txt, NORM_TXT)) * SCALE_BOW;
		return sapc < LO_CLIP ? 0 : (sapc - LO_BOW_OFFSET) * 100;
	}
	const sapc = (Math.pow(bg, REV_BG) - Math.pow(txt, REV_TXT)) * SCALE_WOB;
	return sapc > -LO_CLIP ? 0 : (sapc + LO_WOB_OFFSET) * 100;
};

export const tagLabelColor = (
	background: string,
	fallback: TagLabelColor = 'black',
	surfaceHex: string = TAG_SURFACE_HEX
): TagLabelColor => {
	const hex = normalizeHex(background);
	if (!hex) {
		return fallback;
	}
	const opaque = flattenHex(hex, surfaceHex);
	// Keyed on the colour itself, so an override holds at any opacity.
	const override = TAG_LABEL_OVERRIDES[hex.substring(0, 6)];
	if (override) {
		return override;
	}
	const backgroundY = srgbToY(hexToRgb(opaque));
	const onDark = Math.abs(apcaContrast(DARK_LABEL_Y, backgroundY));
	const onLight = Math.abs(apcaContrast(LIGHT_LABEL_Y, backgroundY));
	return onDark >= onLight ? 'black' : 'white';
};
